import { UserRole } from '../types/permissions';
import { notify } from './globalNotify';
import { logger } from './terminalLogger';

/**
 * Mapeamento único e centralizado de perm_id da API para UserRole do sistema
 * Este é o local único de verdade para conversão de permissões
 */
export const PERMISSION_ID_TO_ROLE: Record<number, UserRole> = {
  2: 'admin',
  3: 'vendedor',
  4: 'producao',
  5: 'revenda',
  6: 'clientes_plus',
  8: 'cliente',
} as const;

/**
 * Consulta silenciosa do mapeamento de permissão para role.
 * Não emite notify/log de erro quando o perm_id não é suportado pela UI.
 */
export function getMappedRoleByPermissionId(permId: number): UserRole | null {
  return PERMISSION_ID_TO_ROLE[permId] ?? null;
}

/**
 * Converte perm_id da API para UserRole do sistema.
 * Retorna null quando o perm_id não está mapeado e emite notificação de erro.
 * @param permId - ID da permissão vindo da API
 * @returns UserRole correspondente, ou null se desconhecido
 */
export function mapPermissionIdToRole(permId: number): UserRole | null {
  const mappedRole = getMappedRoleByPermissionId(permId);
  if (mappedRole) {
    return mappedRole;
  }

  logger.error('[PERMISSION_MAPPER] perm_id desconhecido', {
    permId,
    validIds: Object.keys(PERMISSION_ID_TO_ROLE),
  });
  notify({
    type: 'error',
    title: 'Permissão inválida',
    message: `O identificador de permissão (perm_id: ${permId}) não é reconhecido pelo sistema.`,
    duration: 5000,
  });
  return null;
}

/**
 * Verifica se um perm_id é válido no sistema
 * @param permId - ID da permissão a ser validado
 * @returns true se o perm_id é válido
 */
export function isValidPermissionId(permId: number): boolean {
  return permId in PERMISSION_ID_TO_ROLE;
}

/**
 * Obtém todos os perm_ids válidos no sistema
 * @returns Array com todos os IDs de permissão válidos
 */
export function getValidPermissionIds(): number[] {
  return Object.keys(PERMISSION_ID_TO_ROLE).map(Number);
}

/**
 * Obtém todos os roles disponíveis no sistema
 * @returns Array com todos os UserRoles válidos
 */
export function getAvailableRoles(): UserRole[] {
  return Object.values(PERMISSION_ID_TO_ROLE);
}

/**
 * Mapeamento inverso: role → perm_id
 * Usado para buscar permissões da API quando simulando fluxo
 */
export const ROLE_TO_PERMISSION_ID: Record<UserRole, number> = {
  admin: 2,
  vendedor: 3,
  producao: 4,
  revenda: 5,
  clientes_plus: 6,
  cliente: 8
} as const;

/**
 * Converte UserRole para perm_id da API
 * @param role - Role do sistema
 * @returns perm_id correspondente ou null se não encontrado
 */
export function roleToPermissionId(role: UserRole): number | null {
  return ROLE_TO_PERMISSION_ID[role] ?? null;
}

// DEPRECATED: Use mapPermissionIdToRole instead
// Mantido para compatibilidade durante migração
export const permissionIdToRole = PERMISSION_ID_TO_ROLE;
